import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Mail, User, Shield, AlertCircle, Copy, Check, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUsers, createUser, deleteUser, generatePasswordResetLink } from '../services/userService';
import toast from 'react-hot-toast';

interface UserData {
  id: string;
  email: string;
  name?: string;
  role: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSetupLink, setShowSetupLink] = useState(false);
  const [setupLink, setSetupLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isGeneratingReset, setIsGeneratingReset] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'coach'
  });

  useEffect(() => {
    // Check if user is authorized
    if (!user || (user.role !== 'superadmin' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
    loadUsersData();
  }, [user, navigate]);

  const loadUsersData = async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await getUsers(user.email);
      setUsers(data);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      const { setupUrl } = await createUser(formData, user.email);
      setSetupLink(setupUrl);
      setShowSetupLink(true);
      setShowCreateModal(false);
      await loadUsersData();
      toast.success('User created successfully');
    } catch (err: any) {
      console.error('Error creating user:', err);
      toast.error(err.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!user) return;
    try {
      await deleteUser(userId, user.email);
      setUsers(users.filter(u => u.id !== userId));
      setShowDeleteConfirm(null);
      toast.success('User deleted successfully');
    } catch (err: any) {
      console.error('Error deleting user:', err);
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleGenerateResetLink = async (userId: string) => {
    if (!user) return;
    try {
      setIsGeneratingReset(userId);
      const resetUrl = await generatePasswordResetLink(userId, user.email);
      setSetupLink(resetUrl);
      setShowSetupLink(true);
      toast.success('Password reset link generated');
    } catch (err: any) {
      console.error('Error generating reset link:', err);
      toast.error(err.message || 'Failed to generate reset link');
    } finally {
      setIsGeneratingReset(null);
    }
  };

  const copySetupLink = () => {
    navigator.clipboard.writeText(setupLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
        toast.success('Link copied to clipboard');
      })
      .catch(() => toast.error('Failed to copy link'));
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'coach':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={loadUsersData}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <UserPlus size={20} className="mr-2" />
          Add User
        </button>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3 text-sm text-blue-800">
            User management has moved to the <strong>Supabase Dashboard</strong> (Authentication →
            Users) for security. Create users there and set their <code>role</code> in User
            Metadata; generate password/recovery links from the same screen and share them. The
            list below is read-only.
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Users</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <User size={16} className="text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2 justify-end">
                        <button
                          onClick={() => handleGenerateResetLink(user.id)}
                          className="text-navy-600 hover:text-navy-900"
                          disabled={isGeneratingReset === user.id}
                        >
                          <RefreshCw size={18} className={isGeneratingReset === user.id ? 'animate-spin' : ''} />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label flex items-center">
                      <User size={16} className="mr-2" />
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label flex items-center">
                      <Mail size={16} className="mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="role" className="form-label flex items-center">
                      <Shield size={16} className="mr-2" />
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      className="form-input"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="coach">Coach</option>
                      {user?.role === 'superadmin' && (
                        <>
                          <option value="admin">Admin</option>
                          <option value="superadmin">Super Admin</option>
                        </>
                      )}
                      {user?.role === 'admin' && (
                        <option value="admin">Admin</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
                <button
                  type="button"
                  className="btn-outline mr-2"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Setup/Reset Link Modal */}
      {showSetupLink && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Password Setup Link</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Share this link with the user to set up their password:
              </p>
              <div className="flex items-center bg-gray-50 p-3 rounded-md">
                <input
                  type="text"
                  value={setupLink}
                  className="flex-1 bg-transparent border-none focus:ring-0 text-sm text-gray-600"
                  readOnly
                />
                <button
                  type="button"
                  onClick={copySetupLink}
                  className="ml-2 text-navy-600 hover:text-navy-800"
                >
                  {linkCopied ? (
                    <Check size={20} className="text-green-500" />
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowSetupLink(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
              <button
                type="button"
                className="btn-outline mr-2"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition-colors active:bg-red-800"
                onClick={() => handleDeleteUser(showDeleteConfirm)}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;